import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  Logout,
  Dashboard as DashboardIcon,
  Person,
  Security,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const DummyPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
  };

  const handleGoToDashboard = () => {
    navigate('/');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
          <Typography variant="h3" component="h1" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
            Login Successful! 🎉
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            Welcome to the Phanova CCM Platform
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* User Information Card */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" color="primary">
                  User Information
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {user?.email || 'No email provided'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {user?.name || 'No name provided'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    User ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                    {user?.id || 'No ID provided'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Role
                  </Typography>
                  <Chip 
                    label={user?.role || 'No role'} 
                    color="primary"
                    size="small"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Authentication Status Card */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Security sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" color="primary">
                  Authentication Status
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Authentication Provider
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Auth0 (via Backend)
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Login Method
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Backend Server Redirect
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Session Status
                  </Typography>
                  <Chip 
                    label="Authenticated" 
                    color="success"
                    size="small"
                    icon={<CheckCircle />}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Debug Information Card */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Debug Information
              </Typography>
              <Paper 
                sx={{ 
                  p: 2, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1, 
                  maxHeight: 200, 
                  overflow: 'auto' 
                }}
              >
                <Typography 
                  component="pre" 
                  sx={{ 
                    fontSize: '0.8rem', 
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all'
                  }}
                >
                  {JSON.stringify({
                    user,
                    currentUrl: window.location.href,
                    timestamp: new Date().toISOString(),
                    authMethod: 'Backend Auth0 Integration'
                  }, null, 2)}
                </Typography>
              </Paper>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<DashboardIcon />}
                  onClick={handleGoToDashboard}
                  sx={{
                    background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1565c0 30%, #2196f3 90%)',
                    },
                  }}
                >
                  Go to Dashboard
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Logout />}
                  onClick={handleLogout}
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'primary.light',
                    },
                  }}
                >
                  Logout
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            🎯 Auth0 Backend Integration Test - Phanova CCM Platform
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default DummyPage;